import type { JSX } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EvaluateSubmissionDialog } from '@/features/memorization/evaluate-submission-dialog';
import { EvaluationHistory } from '@/features/memorization/evaluation-history';
import { useSubmission } from '@/features/memorization/hooks';
import { SUBMISSION_STATUS_LABELS } from '@/features/memorization/labels';
import { useHasCapability } from '@/lib/capabilities';

/**
 * Setoran detail (Phase G-2, Job 12) - the submission's own fields plus
 * its evaluation history, filtered to just this submission. Only what
 * `GET /memorization/setoran/:id` actually returns is shown; there is no
 * edit or delete action anywhere on this page because none exists on the
 * backend (a submission is immutable once created).
 */
export function SubmissionDetailPage(): JSX.Element {
  const { studentId, submissionId } = useParams<{ studentId: string; submissionId: string }>();
  const [evaluateOpen, setEvaluateOpen] = useState(false);
  const { data: submission, isLoading, isError } = useSubmission(submissionId ?? '');
  const canEvaluate = useHasCapability('evaluations:manage');

  if (studentId === undefined || submissionId === undefined) {
    return <p className="p-4 text-sm text-destructive">Setoran tidak ditemukan.</p>;
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      {isLoading && <Skeleton className="h-32 w-full" />}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat setoran.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && submission !== undefined && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle>
                Surah {submission.surah}: Ayat {submission.startAyah}
                {submission.endAyah !== submission.startAyah ? `-${submission.endAyah}` : ''}
              </CardTitle>
              <Badge variant={submission.status === 'EVALUATED' ? 'default' : 'secondary'}>
                {SUBMISSION_STATUS_LABELS[submission.status]}
              </Badge>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                Disetorkan {new Date(submission.submittedAt).toLocaleString('id-ID')}
                {submission.teacherId !== null ? ' · Dicatat oleh guru' : ''}
              </p>

              {canEvaluate && submission.status === 'AWAITING_EVALUATION' && (
                <Button
                  type="button"
                  size="sm"
                  className="w-fit"
                  onClick={() => {
                    setEvaluateOpen(true);
                  }}
                >
                  Evaluasi Setoran
                </Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Evaluasi</CardTitle>
            </CardHeader>
            <CardContent>
              <EvaluationHistory targetId={submission.targetId} submissionId={submission.id} />
            </CardContent>
          </Card>

          <EvaluateSubmissionDialog
            submission={submission}
            open={evaluateOpen}
            onOpenChange={setEvaluateOpen}
          />
        </>
      )}
    </div>
  );
}
